import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { flatMap } from "lodash";
import moment, { Moment } from "moment";

import { getRequestStateForChat } from "selectors/chat/chat";
import { getMessagesForChat } from "selectors/chat/messages";

import ScrollContainer from "../shared/ScrollContainer";
import ChatMessage from "./ChatMessage";
import DaySeparator from "./DaySeparator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";

export default function Chat({ id }: { id: string }) {
  const { loadingMessages, moreMessagesAvailable } = useSelector((state) =>
    getRequestStateForChat(state, { chatId: id }),
  );
  const messages = useSelector((state) =>
    getMessagesForChat(state, { chatId: id }),
  );
  let lastMessageFrom: string | null = null;
  let lastMessageDate: Moment | null = null;

  const dispatch = useDispatch();
  function onStartReached() {
    dispatch({
      type: "chat/CHAT/SCROLL_START_REACHED",
      chatId: id,
    });
  }
  function onEndReached() {
    dispatch({
      type: "chat/CHAT/SCROLL_END_REACHED",
      chatId: id,
    });
  }

  return (
    <ScrollContainer
      id={id}
      className="chat__messages_container"
      onStartReached={onStartReached}
      onEndReached={onEndReached}
      callbackThreshold={2}
      autoscroll
      autoscrollThreashold={10}
      startElement={
        moreMessagesAvailable ? null : (
          <div className="chatNoMoreMessages text-center text-muted text-sm mt-2">
            {I18n.t("js.chat.room.no_more_messages")}
          </div>
        )
      }
    >
      {loadingMessages && (
        <div className="chatLoadMessagesIndicator text-center text-muted py-2">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {flatMap(messages, (message, i) => {
        const result: React.ReactElement[] = [];
        const sameAuthor = lastMessageFrom === message.from.id;
        const sameDay = moment(message.created_at).isSame(
          lastMessageDate,
          "day",
        );

        if (!sameDay) {
          result.push(
            <DaySeparator
              key={message.created_at || `sep+${i}`}
              date={message.created_at}
            />,
          );
        }

        result.push(
          <ChatMessage
            key={i}
            groupedMessage={sameAuthor && sameDay}
            {...message}
          />,
        );

        lastMessageFrom = message.from.id;
        lastMessageDate = moment(message.created_at);

        return result;
      })}
    </ScrollContainer>
  );
}
